import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config/api';
import { X, Printer, Edit3, Save, XCircle } from 'lucide-react';

// Professional blue palette
const COLORS = {
    primary: '#1a3c6e',
    primaryLight: '#2c5aa0',
    accent: '#0d2b52',
    headerBg: '#1a3c6e',
    headerText: '#ffffff',
    tableBorder: '#c8d6e5',
    tableHeader: '#eaf0f7',
    tableHeaderText: '#1a3c6e',
    highlight: '#2c5aa0',
    lightBg: '#f4f7fb',
    darkText: '#1a2332',
    mutedText: '#5a6f85',
    totalBg: '#1a3c6e',
    totalText: '#ffffff',
    gstBadge: '#e8f0fe',
    gstText: '#1a3c6e',
};

const InvoiceView = ({ invoiceId, invoiceNumber, referenceId, invoiceType, onClose, isAdmin = false }) => {
    const [invoice, setInvoice] = useState(null);
    const [billSettings, setBillSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const printRef = useRef();

    useEffect(() => {
        fetchInvoice();
        // eslint-disable-next-line
    }, [invoiceId, invoiceNumber, referenceId, invoiceType]);

    const fetchInvoice = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const apiPath = isAdmin ? '/admin/invoices.php' : '/user/invoices.php';
            let url = `${API_BASE_URL}${apiPath}?`;
            if (invoiceId) url += `id=${invoiceId}`;
            else if (invoiceNumber) url += `number=${invoiceNumber}`;
            else if (referenceId && invoiceType) url += `reference_id=${referenceId}&type=${invoiceType}`;

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setInvoice(data.data);
                setBillSettings(data.bill_settings || {});
                setEditData(data.data);
            }
        } catch (error) {
            console.error('Error fetching invoice:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Invoice ${invoice?.invoice_number || ''}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 15px; background: #fff; color: #1a2332; }
                    @media print { body { padding: 0; } .no-print { display: none !important; } }
                </style>
            </head>
            <body>
                ${printContent.innerHTML}
                <script>window.onload = function() { window.print(); window.close(); }</script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/invoices.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'update',
                    invoice_id: invoice.id,
                    ...editData
                })
            });
            const data = await response.json();
            if (data.success) {
                setInvoice(editData);
                setEditing(false);
            }
        } catch (error) {
            console.error('Error saving invoice:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...(editData.items || [])];
        newItems[index] = { ...newItems[index], [field]: value };
        if (field === 'quantity' || field === 'rate') {
            const qty = field === 'quantity' ? parseFloat(value) : parseFloat(newItems[index].quantity);
            const rate = field === 'rate' ? parseFloat(value) : parseFloat(newItems[index].rate);
            newItems[index].amount = qty * rate;
        }
        // GST INCLUSIVE: base amount is what's shown in items, taxes are extracted from total
        const subtotal = newItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        const totalTaxRate = parseFloat(editData.cgst_rate || 0) + parseFloat(editData.sgst_rate || 0) + parseFloat(editData.igst_rate || 0);
        const totalAmount = subtotal * (1 + totalTaxRate / 100);
        const cgstAmount = subtotal * parseFloat(editData.cgst_rate || 0) / 100;
        const sgstAmount = subtotal * parseFloat(editData.sgst_rate || 0) / 100;
        const igstAmount = subtotal * parseFloat(editData.igst_rate || 0) / 100;
        setEditData({
            ...editData,
            items: newItems,
            subtotal: subtotal.toFixed(2),
            cgst_amount: cgstAmount.toFixed(2),
            sgst_amount: sgstAmount.toFixed(2),
            igst_amount: igstAmount.toFixed(2),
            total_amount: totalAmount.toFixed(2)
        });
    };

    const addItem = () => {
        const items = [...(editData.items || [])];
        items.push({ sr_no: items.length + 1, description: '', quantity: 1, rate: 0, amount: 0 });
        setEditData({ ...editData, items });
    };

    const removeItem = (index) => {
        const items = (editData.items || []).filter((_, i) => i !== index);
        items.forEach((item, i) => item.sr_no = i + 1);
        const subtotal = items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        setEditData({ ...editData, items, subtotal: subtotal.toFixed(2) });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                <div className="bg-white rounded-2xl p-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800 mx-auto"></div>
                    <p className="text-gray-500 mt-4 text-center">Loading invoice...</p>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
                    <p className="text-gray-500 mb-4">Invoice not found</p>
                    <button onClick={onClose} className="px-6 py-2 bg-gray-900 text-white rounded-xl font-bold">Close</button>
                </div>
            </div>
        );
    }

    const data = editing ? editData : invoice;
    const companyName = billSettings.bill_company_name || 'SWIFT2AI LIFECARE PVT LTD';
    const gstNumber = billSettings.bill_gst_number || '';
    const headerText = billSettings.bill_header_text || '';
    const gstNote = billSettings.bill_gst_note || '';
    const footerText = billSettings.bill_footer_text || '';
    const logoUrl = billSettings.bill_logo_url || '';
    const companyAddress = billSettings.company_address || '';

    const hasTax = parseFloat(data.cgst_amount) > 0 || parseFloat(data.sgst_amount) > 0 || parseFloat(data.igst_amount) > 0;

    const editInputStyle = {
        border: `1px solid ${COLORS.tableBorder}`,
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontFamily: "'Segoe UI', sans-serif",
        outline: 'none',
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-2 sm:p-4 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[95vh] flex flex-col overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 no-print"
                    style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})` }}>
                    <h3 className="font-black text-sm sm:text-lg tracking-tight text-white">
                        📄 Invoice #{data.invoice_number}
                    </h3>
                    <div className="flex items-center gap-2">
                        {isAdmin && !editing && (
                            <button onClick={() => { setEditData({ ...invoice }); setEditing(true); }}
                                className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors text-white" title="Edit Invoice">
                                <Edit3 size={16} />
                            </button>
                        )}
                        {editing && (
                            <>
                                <button onClick={handleSave} disabled={saving}
                                    className="px-3 py-1.5 bg-green-500 hover:bg-green-600 rounded-xl transition-colors text-xs font-bold flex items-center gap-1 text-white">
                                    <Save size={14} /> {saving ? 'Saving...' : 'Save'}
                                </button>
                                <button onClick={() => { setEditing(false); setEditData({ ...invoice }); }}
                                    className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors text-white">
                                    <XCircle size={16} />
                                </button>
                            </>
                        )}
                        <button onClick={handlePrint} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors text-white" title="Print">
                            <Printer size={16} />
                        </button>
                        <button onClick={onClose} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors text-white">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Invoice Content */}
                <div className="overflow-y-auto flex-1 p-2 sm:p-5 bg-gray-50" ref={printRef}>
                    <div style={{
                        maxWidth: '760px',
                        margin: '0 auto',
                        background: '#fff',
                        fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                        border: `2px solid ${COLORS.primary}`,
                        borderRadius: '4px',
                        overflow: 'hidden',
                    }}>
                        {/* === TOP HEADER BAR === */}
                        <div style={{
                            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                            padding: '20px 25px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '15px',
                        }}>
                            {/* Logo + Company */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                                {logoUrl && (
                                    <div style={{
                                        background: '#fff',
                                        borderRadius: '10px',
                                        padding: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '60px',
                                        minHeight: '60px',
                                    }}>
                                        <img src={logoUrl} alt="Logo" style={{ maxHeight: '50px', maxWidth: '100px', objectFit: 'contain' }} />
                                    </div>
                                )}
                                <div>
                                    <div style={{
                                        fontSize: '20px',
                                        fontWeight: '800',
                                        color: '#fff',
                                        letterSpacing: '1px',
                                        lineHeight: 1.2,
                                    }}>
                                        {companyName}
                                    </div>
                                    {companyAddress && (
                                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px', marginTop: '4px', maxWidth: '350px' }}>
                                            {companyAddress}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Invoice Label */}
                            <div style={{ textAlign: 'right' }}>
                                <div style={{
                                    fontSize: '24px',
                                    fontWeight: '900',
                                    color: 'rgba(255,255,255,0.3)',
                                    letterSpacing: '3px',
                                    textTransform: 'uppercase',
                                }}>INVOICE</div>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    color: '#fff',
                                    marginTop: '2px',
                                }}>{data.invoice_number}</div>
                            </div>
                        </div>

                        {/* === HEADER TEXT (sub-heading) === */}
                        {headerText && (
                            <div style={{
                                padding: '8px 25px',
                                background: COLORS.lightBg,
                                borderBottom: `1px solid ${COLORS.tableBorder}`,
                                textAlign: 'center',
                            }}>
                                <div style={{ fontSize: '11px', fontWeight: '600', color: COLORS.mutedText, letterSpacing: '0.3px' }}>
                                    {headerText}
                                </div>
                            </div>
                        )}

                        {/* === GST BAR === */}
                        {gstNumber && (
                            <div style={{
                                padding: '8px 25px',
                                borderBottom: `1px solid ${COLORS.tableBorder}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                flexWrap: 'wrap',
                            }}>
                                <span style={{
                                    background: COLORS.gstBadge,
                                    color: COLORS.gstText,
                                    padding: '3px 12px',
                                    borderRadius: '20px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                }}>GST: {gstNumber}</span>
                                {gstNote && (
                                    <span style={{ color: COLORS.mutedText, fontSize: '10px', fontStyle: 'italic' }}>
                                        {gstNote}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* === CUSTOMER INFO ROW === */}
                        <div style={{
                            padding: '18px 25px',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px',
                            borderBottom: `2px solid ${COLORS.primary}`,
                        }}>
                            {/* Left: Bill To */}
                            <div>
                                <div style={{
                                    fontSize: '9px',
                                    fontWeight: '800',
                                    color: COLORS.primaryLight,
                                    textTransform: 'uppercase',
                                    letterSpacing: '2px',
                                    marginBottom: '8px',
                                }}>BILL TO</div>
                                <div style={{ fontSize: '15px', fontWeight: '700', color: COLORS.darkText }}>
                                    {editing ? (
                                        <input type="text" value={editData.customer_name || ''} onChange={e => setEditData({ ...editData, customer_name: e.target.value })}
                                            style={{ ...editInputStyle, width: '100%', fontSize: '14px', fontWeight: '700' }} />
                                    ) : data.customer_name}
                                </div>
                                <div style={{ fontSize: '12px', color: COLORS.mutedText, marginTop: '3px' }}>
                                    ID: {editing ? (
                                        <input type="text" value={editData.customer_id || ''} onChange={e => setEditData({ ...editData, customer_id: e.target.value })}
                                            style={{ ...editInputStyle, width: '120px' }} />
                                    ) : <strong style={{ color: COLORS.primary }}>{data.customer_id}</strong>}
                                </div>
                                <div style={{ fontSize: '11px', color: COLORS.mutedText, marginTop: '4px', lineHeight: 1.5 }}>
                                    {editing ? (
                                        <textarea value={editData.customer_address || ''} onChange={e => setEditData({ ...editData, customer_address: e.target.value })}
                                            rows={2} style={{ ...editInputStyle, width: '100%', resize: 'vertical' }} />
                                    ) : data.customer_address}
                                </div>
                            </div>
                            {/* Right: Invoice Details */}
                            <div style={{ textAlign: 'right' }}>
                                <div style={{
                                    fontSize: '9px',
                                    fontWeight: '800',
                                    color: COLORS.primaryLight,
                                    textTransform: 'uppercase',
                                    letterSpacing: '2px',
                                    marginBottom: '8px',
                                }}>INVOICE DETAILS</div>
                                <table style={{ marginLeft: 'auto', borderSpacing: '4px' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ fontSize: '11px', color: COLORS.mutedText, paddingRight: '12px', textAlign: 'right' }}>Date</td>
                                            <td style={{ fontSize: '12px', fontWeight: '600', color: COLORS.darkText }}>
                                                {editing ? (
                                                    <input type="date" value={editData.invoice_date || ''} onChange={e => setEditData({ ...editData, invoice_date: e.target.value })}
                                                        style={{ ...editInputStyle }} />
                                                ) : new Date(data.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontSize: '11px', color: COLORS.mutedText, paddingRight: '12px', textAlign: 'right' }}>Receipt No.</td>
                                            <td style={{ fontSize: '12px', fontWeight: '700', color: COLORS.primary }}>{data.invoice_number}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontSize: '11px', color: COLORS.mutedText, paddingRight: '12px', textAlign: 'right' }}>Type</td>
                                            <td style={{ fontSize: '12px', fontWeight: '600', color: COLORS.darkText, textTransform: 'capitalize' }}>
                                                {data.invoice_type === 'epin' ? 'E-Pin' : 'Product'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* === ITEMS TABLE === */}
                        <div style={{ padding: '0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ padding: '12px 12px', textAlign: 'center', fontSize: '10px', fontWeight: '800', color: COLORS.tableHeaderText, background: COLORS.tableHeader, borderBottom: `2px solid ${COLORS.primary}`, width: '50px', textTransform: 'uppercase', letterSpacing: '1px' }}>#</th>
                                        <th style={{ padding: '12px 12px', textAlign: 'left', fontSize: '10px', fontWeight: '800', color: COLORS.tableHeaderText, background: COLORS.tableHeader, borderBottom: `2px solid ${COLORS.primary}`, textTransform: 'uppercase', letterSpacing: '1px' }}>Description</th>
                                        <th style={{ padding: '12px 12px', textAlign: 'center', fontSize: '10px', fontWeight: '800', color: COLORS.tableHeaderText, background: COLORS.tableHeader, borderBottom: `2px solid ${COLORS.primary}`, width: '70px', textTransform: 'uppercase', letterSpacing: '1px' }}>Qty</th>
                                        <th style={{ padding: '12px 12px', textAlign: 'right', fontSize: '10px', fontWeight: '800', color: COLORS.tableHeaderText, background: COLORS.tableHeader, borderBottom: `2px solid ${COLORS.primary}`, width: '100px', textTransform: 'uppercase', letterSpacing: '1px' }}>Rate (₹)</th>
                                        <th style={{ padding: '12px 12px', textAlign: 'right', fontSize: '10px', fontWeight: '800', color: COLORS.tableHeaderText, background: COLORS.tableHeader, borderBottom: `2px solid ${COLORS.primary}`, width: '110px', textTransform: 'uppercase', letterSpacing: '1px' }}>Amount (₹)</th>
                                        {editing && <th style={{ width: '35px', background: COLORS.tableHeader, borderBottom: `2px solid ${COLORS.primary}` }}></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.items || []).map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.tableBorder}` }}>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'center', color: COLORS.mutedText, fontWeight: '600' }}>{item.sr_no}</td>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', color: COLORS.darkText, fontWeight: '500' }}>
                                                {editing ? (
                                                    <input type="text" value={editData.items?.[idx]?.description || ''} onChange={e => handleItemChange(idx, 'description', e.target.value)}
                                                        style={{ ...editInputStyle, width: '100%' }} />
                                                ) : item.description}
                                            </td>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'center', color: COLORS.darkText }}>
                                                {editing ? (
                                                    <input type="number" value={editData.items?.[idx]?.quantity || ''} onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                                                        style={{ ...editInputStyle, width: '50px', textAlign: 'center' }} />
                                                ) : item.quantity}
                                            </td>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'right', color: COLORS.darkText }}>
                                                {editing ? (
                                                    <input type="number" step="0.01" value={editData.items?.[idx]?.rate || ''} onChange={e => handleItemChange(idx, 'rate', e.target.value)}
                                                        style={{ ...editInputStyle, width: '80px', textAlign: 'right' }} />
                                                ) : `₹${parseFloat(item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                                            </td>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'right', color: COLORS.darkText, fontWeight: '600' }}>
                                                ₹{parseFloat(editing ? (editData.items?.[idx]?.amount || 0) : item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                            {editing && (
                                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                                    <button onClick={() => removeItem(idx)} style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>×</button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}

                                    {editing && (
                                        <tr>
                                            <td colSpan="6" style={{ padding: '10px', textAlign: 'center' }}>
                                                <button onClick={addItem} style={{ color: COLORS.primaryLight, background: 'none', border: `1px dashed ${COLORS.primaryLight}`, padding: '5px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>+ Add Item</button>
                                            </td>
                                        </tr>
                                    )}

                                    {/* Subtotal & Tax */}
                                    {hasTax && (
                                        <>
                                            <tr style={{ background: COLORS.lightBg }}>
                                                <td colSpan={editing ? 5 : 4} style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.mutedText }}>Subtotal</td>
                                                <td style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.darkText }}>₹{parseFloat(data.subtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                            {parseFloat(data.cgst_amount) > 0 && (
                                                <tr>
                                                    <td colSpan={editing ? 5 : 4} style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: COLORS.mutedText }}>CGST ({data.cgst_rate}%)</td>
                                                    <td style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: COLORS.darkText }}>₹{parseFloat(data.cgst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            )}
                                            {parseFloat(data.sgst_amount) > 0 && (
                                                <tr>
                                                    <td colSpan={editing ? 5 : 4} style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: COLORS.mutedText }}>SGST ({data.sgst_rate}%)</td>
                                                    <td style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: COLORS.darkText }}>₹{parseFloat(data.sgst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            )}
                                            {parseFloat(data.igst_amount) > 0 && (
                                                <tr>
                                                    <td colSpan={editing ? 5 : 4} style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: COLORS.mutedText }}>IGST ({data.igst_rate}%)</td>
                                                    <td style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: COLORS.darkText }}>₹{parseFloat(data.igst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            )}
                                        </>
                                    )}

                                    {/* GRAND TOTAL */}
                                    <tr>
                                        <td colSpan={editing ? 5 : 4} style={{
                                            padding: '14px 12px',
                                            fontSize: '14px',
                                            textAlign: 'right',
                                            fontWeight: '800',
                                            color: '#fff',
                                            background: COLORS.totalBg,
                                            letterSpacing: '0.5px',
                                        }}>TOTAL AMOUNT {hasTax && <span style={{ fontSize: '10px', fontWeight: '600', opacity: 0.8 }}>(Incl. GST)</span>}</td>
                                        <td style={{
                                            padding: '14px 12px',
                                            fontSize: '16px',
                                            textAlign: 'right',
                                            fontWeight: '900',
                                            color: '#fff',
                                            background: COLORS.totalBg,
                                        }}>₹{parseFloat(data.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* === AMOUNT IN WORDS === */}
                        <div style={{
                            padding: '12px 25px',
                            background: COLORS.lightBg,
                            borderTop: `1px solid ${COLORS.tableBorder}`,
                        }}>
                            <div style={{ fontSize: '10px', fontWeight: '700', color: COLORS.primaryLight, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>
                                Amount in Words
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.darkText, fontStyle: 'italic' }}>
                                {editing ? (
                                    <input type="text" value={editData.amount_in_words || ''} onChange={e => setEditData({ ...editData, amount_in_words: e.target.value })}
                                        style={{ ...editInputStyle, width: '100%', fontSize: '12px' }} />
                                ) : data.amount_in_words}
                            </div>
                        </div>

                        {/* === FOOTER === */}
                        <div style={{
                            padding: '15px 25px',
                            borderTop: `1px solid ${COLORS.tableBorder}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            flexWrap: 'wrap',
                            gap: '15px',
                        }}>
                            {/* GST badge */}
                            <div>
                                {gstNumber && (
                                    <div style={{
                                        background: COLORS.gstBadge,
                                        color: COLORS.gstText,
                                        padding: '5px 14px',
                                        borderRadius: '20px',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        display: 'inline-block',
                                    }}>GST: {gstNumber}</div>
                                )}
                                {footerText && (
                                    <div style={{ fontSize: '10px', color: COLORS.mutedText, marginTop: '6px', maxWidth: '350px', lineHeight: 1.4 }}>
                                        {footerText}
                                    </div>
                                )}
                            </div>
                            {/* Authorized Signature */}
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ borderTop: `1px solid ${COLORS.tableBorder}`, paddingTop: '6px', minWidth: '150px' }}>
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: COLORS.mutedText }}>Authorized Signatory</div>
                                    <div style={{ fontSize: '11px', fontWeight: '700', color: COLORS.primary, marginTop: '2px' }}>{companyName}</div>
                                </div>
                            </div>
                        </div>

                        {/* === BOTTOM BAR === */}
                        <div style={{
                            background: COLORS.primary,
                            padding: '8px 25px',
                            textAlign: 'center',
                            fontSize: '9px',
                            color: 'rgba(255,255,255,0.5)',
                            letterSpacing: '1px',
                        }}>
                            This is a computer-generated invoice.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceView;
